/**
 * Billing Service
 * 
 * Handles API calls for B2C subscription management, payments, and billing.
 * Connected to backend billing API endpoints.
 */
import http from './http'
import type {
  BillingOverview,
  Subscription,
  PaymentMethod,
  PaymentRecord,
  UsageLimits,
  ContactFormData,
  PlanType,
  BillingCycle,
  PaymentLinkResponse,
  SubscriptionStatusResponse,
  TransactionHistoryResponse,
  CancelSubscriptionResponse
} from '../types/billing'

// Environment-based mock data toggle (set to false for production)
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_BILLING === 'true'

// Mock data for development/testing
const MOCK_FREE_USER: BillingOverview = {
  subscription: null,
  paymentMethod: null,
  paymentHistory: [],
  usage: {
    postsPerMonth: 2,
    editsPerMonth: 5,
    imagesPerMonth: 2,
    currentPosts: 1,
    currentEdits: 3,
    currentImages: 1
  }
}

const MOCK_PRO_USER: BillingOverview = {
  subscription: {
    id: 'sub_123',
    planType: 'pro',
    planName: 'Pro (monthly)',
    price: 39.99,
    billingCycle: 'monthly',
    status: 'active',
    startDate: '2025-01-01',
    renewalDate: '2025-02-25'
  },
  paymentMethod: {
    id: 'pm_123',
    brand: 'visa',
    last4: '4331',
    expiryMonth: 9,
    expiryYear: 2029,
    isDefault: true
  },
  paymentHistory: [
    {
      id: 'pay_001',
      service: 'Noovox platform- Pro',
      amount: 39.99,
      currency: 'USD',
      date: '2025-04-25',
      status: 'completed',
      invoiceUrl: '/invoices/001.pdf'
    }
  ],
  usage: {
    postsPerMonth: 12,
    editsPerMonth: 30,
    imagesPerMonth: 8,
    currentPosts: 8,
    currentEdits: 22,
    currentImages: 5
  },
  nextPaymentDate: '2025-05-25',
  nextPaymentAmount: 39.99
}

// Toggle this to test different states in mock mode
const MOCK_IS_SUBSCRIBED = false

class BillingService {
  private readonly baseUrl = '/api/billing'

  /**
   * Get payment link for PayPlus checkout
   * Returns URL to embed in iframe for payment processing
   * @param period - Billing period: 'monthly' or 'annual'
   */
  async getPaymentLink(period: BillingCycle): Promise<PaymentLinkResponse> {
    if (USE_MOCK_DATA) {
      await new Promise(resolve => setTimeout(resolve, 500))
      return {
        payment_url: 'https://payments.payplus.co.il/mock-payment-page',
        page_request_id: 'mock_page_123',
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString()
      }
    }

    try {
      const response = await http.post<PaymentLinkResponse>(`${this.baseUrl}/payment-link/${period}`)
      return response.data
    } catch (error: any) {
      console.error('[BillingService.getPaymentLink] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get subscription status from backend
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    if (USE_MOCK_DATA) {
      await new Promise(resolve => setTimeout(resolve, 300))
      if (MOCK_IS_SUBSCRIBED) {
        return {
          is_subscribed: true,
          subscription_id: 'sub_123',
          current_period_start: '2025-01-01T00:00:00Z',
          current_period_end: '2025-02-01T00:00:00Z',
          status: 'active',
          plan: 'Pro',
          tier: 'pro',
          usage: {
            posts: { limit: 12, used: 8, remaining: 4 },
            comments: { limit: 5, used: 0, remaining: 5 },
            edits: { limit: 30, used: 22, remaining: 8 },
            images: { limit: 8, used: 5, remaining: 3 },
            topics: { limit: 12, used: 3, remaining: 9 },
            topic_search: { limit: false, used: 0, remaining: false }
          }
        }
      }
      return {
        is_subscribed: false,
        subscription_id: null,
        current_period_start: null,
        current_period_end: null,
        status: null,
        plan: 'Free',
        tier: 'free',
        usage: {
          posts: { limit: 2, used: 2, remaining: 0 },
          comments: { limit: 5, used: 0, remaining: 5 },
          edits: { limit: 5, used: 5, remaining: 0 },
          images: { limit: 2, used: 0, remaining: 2 },
          topics: { limit: 12, used: 3, remaining: 9 },
          topic_search: { limit: false, used: 0, remaining: false }
        }
      }
    }

    try {
      const response = await http.get<SubscriptionStatusResponse>(`${this.baseUrl}/subscription-status`)
      return response.data
    } catch (error: any) {
      // If 404 or 500, treat as no subscription (free plan) - backend may not be fully configured
      if (error.response?.status === 404 || error.response?.status === 500) {
        console.warn('[BillingService.getSubscriptionStatus] Backend error, defaulting to free plan:', error.response?.status)
        return {
          is_subscribed: false,
          subscription_id: null,
          current_period_start: null,
          current_period_end: null,
          status: null
        }
      }
      console.error('[BillingService.getSubscriptionStatus] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get transaction history from backend
   */
  async getTransactionHistory(): Promise<TransactionHistoryResponse> {
    if (USE_MOCK_DATA) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return {
        transactions: MOCK_IS_SUBSCRIBED ? [
          {
            id: 'txn_001',
            amount: 39.99,
            currency: 'ILS',
            status: 'completed',
            processed_at: '2025-01-25T10:30:00Z'
          }
        ] : []
      }
    }

    try {
      const response = await http.get<TransactionHistoryResponse>(`${this.baseUrl}/transactions`)
      return response.data
    } catch (error: any) {
      // If 500, return empty transactions - backend may not be fully configured
      if (error.response?.status === 500) {
        console.warn('[BillingService.getTransactionHistory] Backend error, returning empty transactions:', error.response?.status)
        return { transactions: [] }
      }
      console.error('[BillingService.getTransactionHistory] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Cancel current subscription
   */
  async cancelSubscription(): Promise<CancelSubscriptionResponse> {
    if (USE_MOCK_DATA) {
      await new Promise(resolve => setTimeout(resolve, 500))
      return {
        status: 'success',
        message: 'Subscription cancelled successfully',
        subscription_id: 'sub_123',
        access_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      }
    }

    try {
      const response = await http.post<CancelSubscriptionResponse>(`${this.baseUrl}/subscription/cancel`)
      return response.data
    } catch (error: any) {
      console.error('[BillingService.cancelSubscription] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get billing overview for current user
   * Combines subscription status with other billing data
   */
  async getBillingOverview(): Promise<BillingOverview> {
    if (USE_MOCK_DATA) {
      await new Promise(resolve => setTimeout(resolve, 500))
      return MOCK_IS_SUBSCRIBED ? MOCK_PRO_USER : MOCK_FREE_USER
    }

    try {
      // Fetch subscription status and transactions in parallel
      const [subscriptionStatus, transactionHistory] = await Promise.all([
        this.getSubscriptionStatus(),
        this.getTransactionHistory()
      ])

      // Build billing overview from backend data
      const subscription: Subscription | null = subscriptionStatus.is_subscribed ? {
        id: subscriptionStatus.subscription_id!,
        planType: 'pro', // Backend currently only has pro subscriptions
        planName: 'Pro',
        price: 1, // Price from backend config
        billingCycle: 'monthly',
        status: subscriptionStatus.status || 'active',
        startDate: subscriptionStatus.current_period_start || '',
        renewalDate: subscriptionStatus.current_period_end || undefined
      } : null

      // Convert transactions to payment records
      const paymentHistory: PaymentRecord[] = transactionHistory.transactions.map(txn => ({
        id: txn.id,
        service: 'Noovox Pro',
        amount: txn.amount,
        currency: txn.currency,
        date: txn.processed_at || '',
        status: txn.status
      }))

      return {
        subscription,
        paymentMethod: null, // PayPlus handles payment method storage
        paymentHistory,
        usage: {
          // TODO: Fetch actual usage from backend
          postsPerMonth: subscription ? 12 : 2,
          editsPerMonth: subscription ? 30 : 5,
          imagesPerMonth: subscription ? 8 : 2,
          currentPosts: 0,
          currentEdits: 0,
          currentImages: 0
        }
      }
    } catch (error: any) {
      // If backend fails, return free plan as fallback
      console.warn('[BillingService.getBillingOverview] Backend error, returning free plan fallback:', error)
      return {
        subscription: null,
        paymentMethod: null,
        paymentHistory: [],
        usage: {
          postsPerMonth: 2,
          editsPerMonth: 5,
          imagesPerMonth: 2,
          currentPosts: 0,
          currentEdits: 0,
          currentImages: 0
        }
      }
    }
  }

  /**
   * Get current subscription details
   */
  async getSubscription(): Promise<Subscription | null> {
    const status = await this.getSubscriptionStatus()
    
    if (!status.is_subscribed) {
      return null
    }

    return {
      id: status.subscription_id!,
      planType: 'pro',
      planName: 'Pro',
      price: 1,
      billingCycle: 'monthly',
      status: status.status || 'active',
      startDate: status.current_period_start || '',
      renewalDate: status.current_period_end || undefined
    }
  }

  /**
   * Get payment history
   */
  async getPaymentHistory(): Promise<PaymentRecord[]> {
    const history = await this.getTransactionHistory()
    
    return history.transactions.map(txn => ({
      id: txn.id,
      service: 'Noovox Pro',
      amount: txn.amount,
      currency: txn.currency,
      date: txn.processed_at || '',
      status: txn.status
    }))
  }

  /**
   * Get usage limits and current usage
   */
  async getUsage(): Promise<UsageLimits> {
    const overview = await this.getBillingOverview()
    return overview.usage
  }

  /**
   * Check if user has reached usage limits
   */
  async hasReachedLimit(type: 'posts' | 'edits' | 'images'): Promise<boolean> {
    const usage = await this.getUsage()
    
    switch (type) {
      case 'posts':
        return usage.currentPosts >= usage.postsPerMonth
      case 'edits':
        return usage.currentEdits >= usage.editsPerMonth
      case 'images':
        return usage.currentImages >= usage.imagesPerMonth
      default:
        return false
    }
  }

  /**
   * Submit contact form for Teams inquiry
   */
  async submitContactForm(data: ContactFormData): Promise<void> {
    if (USE_MOCK_DATA) {
      await new Promise(resolve => setTimeout(resolve, 1000))
      console.log('[BillingService.submitContactForm] Mock: Contact form submitted', data)
      return
    }

    try {
      await http.post(`${this.baseUrl}/contact`, data)
    } catch (error: any) {
      console.error('[BillingService.submitContactForm] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Download invoice PDF
   */
  async downloadInvoice(paymentId: string): Promise<Blob> {
    if (USE_MOCK_DATA) {
      await new Promise(resolve => setTimeout(resolve, 500))
      console.log('[BillingService.downloadInvoice] Mock: Downloading invoice', paymentId)
      return new Blob(['Mock PDF content'], { type: 'application/pdf' })
    }

    try {
      const response = await http.get(`${this.baseUrl}/invoices/${paymentId}`, {
        responseType: 'blob'
      })
      return response.data
    } catch (error: any) {
      console.error('[BillingService.downloadInvoice] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get checkout URL - DEPRECATED: Use getPaymentLink() instead
   * Kept for backward compatibility
   */
  async getCheckoutUrl(planType: PlanType, billingCycle: BillingCycle): Promise<string> {
    const paymentLink = await this.getPaymentLink(billingCycle)
    return paymentLink.payment_url
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Error {
    if (error.response) {
      const status = error.response.status
      const detail = error.response.data?.detail || error.response.data?.message

      if (status === 403) {
        return new Error(detail || 'You do not have permission to perform this action')
      }
      if (status === 401) {
        return new Error(detail || 'Authentication required')
      }
      if (status === 400) {
        return new Error(detail || 'Invalid request')
      }
      if (status === 402) {
        return new Error(detail || 'Payment required')
      }
      if (status === 404) {
        return new Error(detail || 'Resource not found')
      }
      if (status === 500) {
        return new Error(detail || 'Server error. Please try again later.')
      }
      return new Error(detail || 'An error occurred')
    }
    return new Error(error.message || 'Network error')
  }
}

export const billingService = new BillingService()
export default billingService
